<template>
  <header-block>
    <!-- The count is the whole point of the button being here: accepting AI
         suggestions changes rows without saving them, and edits survive
         switching category, so the header has to say how much is still only in
         the browser. The tooltip says which categories, since the count alone
         cannot. -->
    <button
      class="btn btn-primary"
      :class="{ 'has-unsaved': dirtyCount > 0 }"
      :disabled="saving"
      :title="
        dirtyCount > 0 ? 'Unsaved — ' + pendingSummary : 'Nothing to save'
      "
      v-on:click="save"
    >
      {{ saving ? "Saving…" : "Save" }}
      <span class="save-count" v-if="dirtyCount > 0 && !saving">
        {{ dirtyCount }}
      </span>
    </button>
  </header-block>
  <div class="page-container">
    <div class="inner-wide translate-grid">
      <div class="menu-side">
        <div class="white-box">
          <h2 class="section-label side-head">Categories</h2>
          <div class="lang-scope">lowest language</div>
          <div class="side-list">
            <button
              v-for="page in pages"
              :key="page.id"
              v-on:click="activePage = page"
              :class="{
                active: activePage?.id === page.id,
                stale: coverageLoading,
              }"
              :title="categoryTitle(page)"
            >
              <span class="side-list-label">{{ page.name }}</span>
              <span
                class="cat-pct"
                :class="coverageClass(page)"
                v-if="categoryPercent(page) !== null"
                >{{ categoryPercent(page) }}%</span
              >
            </button>
          </div>
        </div>
      </div>
      <div class="white-box translate-main" v-if="activePage">
        <div class="card-head">
          <h1>{{ $t("editor.translations") }}</h1>
          <span class="badge-soft badge-green" v-if="words.length > 0">
            {{ activePage?.name }} · {{ donePercent }}%
          </span>
          <div class="head-controls">
            <label class="check-row missing-toggle">
              <input type="checkbox" v-model="onlyMissing" />
              Only missing
            </label>
            <label
              class="check-row missing-toggle"
              v-if="issueCount > 0"
              :title="issueCount + ' translation(s) look wrong'"
            >
              <input type="checkbox" v-model="onlyIssues" />
              Only issues
              <span class="issue-count">{{ issueCount }}</span>
            </label>
            <input
              type="text"
              class="search-input"
              v-model="filter"
              :placeholder="$t('editor.search_keys')"
            />
            <!-- Save writes to the live storefront and replaces the whole set
                 for a file and language. Looking before writing is not a
                 developer nicety here, so the switch sits in plain sight. -->
            <label
              class="check-row dry-toggle"
              :class="{ on: dryRun }"
              title="Build the payload and show the diff, but send nothing"
            >
              <input type="checkbox" v-model="dryRun" />
              Dry run
            </label>
            <!-- Import: a flat JSON ({"cart.basic_data.title": "…"}) for one
                 language fills the rows of every category, like typing them —
                 nothing is written until Save, and the dry run diff shows each
                 change first. The language comes from the file name (de.json)
                 or, failing that, the one open. -->
            <button
              type="button"
              class="import-btn"
              :disabled="importing || saving"
              title="Fill rows from a JSON file — nothing is saved until you press Save"
              v-on:click="$refs.importFile.click()"
            >
              {{ importing ? "Importing…" : "Import JSON" }}
            </button>
            <input
              ref="importFile"
              type="file"
              accept="application/json,.json"
              hidden
              v-on:change="importJson"
            />
            <!-- Bulk AI review. It does not touch the inputs: suggestions are
                 collected into the per-row review strips, so a run can be
                 read through and taken row by row. Nothing is saved either
                 way — that stays the Save button's job. -->
            <template v-if="!aiBulkMode">
              <button
                class="ai-all-btn ai-review-btn"
                :disabled="filteredWords.length === 0"
                v-on:click="aiBulkRun('all')"
                :title="
                  'Ask for a second opinion on all ' +
                  filteredWords.length +
                  ' rows on screen'
                "
              >
                <svg
                  viewBox="0 0 24 24"
                  width="15"
                  height="15"
                  fill="currentColor"
                >
                  <path d="M12 5V2L8 6l4 4V7a5 5 0 1 1-5 5H5a7 7 0 1 0 7-7z" />
                </svg>
                AI review all
              </button>
            </template>
            <div class="ai-running" v-else>
              <span class="app-loader small"></span>
              <span class="ai-progress">
                {{ aiBulkMode === "missing" ? "Translating" : "Reviewing" }}
                {{ aiProgress.done }}/{{ aiProgress.total }}
              </span>
              <!-- A category can be a couple of hundred keys; a run you cannot
                   get out of is a trap. Stopping keeps whatever came back. -->
              <button
                class="ai-stop-btn"
                :disabled="aiStop"
                v-on:click="aiStop = true"
              >
                {{ aiStop ? "Stopping…" : "Stop" }}
              </button>
            </div>
          </div>
        </div>
        <!-- A failed save may well concern a category you cannot see, so the
             message names it instead of just colouring the button. -->
        <div class="save-error" v-if="saveError">{{ saveError }}</div>
        <div class="import-summary" v-if="importSummary">
          <strong>Import · {{ importSummary.lang.toUpperCase() }}</strong> —
          {{ importSummary.changed }} changed, {{ importSummary.added }} new,
          {{ importSummary.same }} unchanged. Nothing is saved yet — press Save
          ({{ dryRun ? "dry run shows the diff first" : "writes live" }}).
          <span class="import-skip" v-if="importSummary.noCategory.length">
            No such category here (needs <code>translation:find</code> first):
            {{ importSummary.noCategory.join(", ") }}.
          </span>
          <span class="import-skip" v-if="importSummary.failed.length">
            Could not load: {{ importSummary.failed.join(", ") }}.
          </span>
          <button class="panel-close" v-on:click="importSummary = null">
            ×
          </button>
        </div>
        <!-- Someone else edited the same category since this browser loaded it.
             The endpoint replaces the whole set, so saving would erase their
             work — and the snapshot could not bring it back either. -->
        <div class="save-conflict" v-if="saveConflicts">
          <div class="dry-diff-head">
            <strong>Not saved — newer changes are available.</strong>
            <button class="panel-close" v-on:click="saveConflicts = null">
              Dismiss
            </button>
          </div>
          <div
            class="save-conflict-cat"
            v-for="c in saveConflicts"
            :key="c.category + c.lang"
          >
            <strong>{{ c.category }} · {{ c.lang }}</strong> —
            {{ c.keys.length }} key{{ c.keys.length === 1 ? "" : "s" }} changed
            since you loaded them:
            <span class="save-conflict-keys">{{ c.keys.join(", ") }}</span>
          </div>
          <div class="save-conflict-hint">
            Reload the page to see the current values, then reapply your edits.
            Your own changes are still here until you do.
          </div>
        </div>
        <!-- Exactly what a save would write, with nothing sent. -->
        <div class="dry-diff" v-if="dryRunDiff">
          <div class="dry-diff-head">
            <strong>Dry run — nothing was sent.</strong>
            <button class="panel-close" v-on:click="dryRunDiff = null">
              Dismiss
            </button>
          </div>
          <div class="dry-diff-empty" v-if="dryRunDiff.length === 0">
            No changes to write.
          </div>
          <div
            class="dry-diff-cat"
            v-for="c in dryRunDiff"
            :key="c.category + c.lang"
          >
            <div class="dry-diff-cat-head">
              {{ c.category }} · {{ c.lang }} — {{ c.changes.length }} change{{
                c.changes.length === 1 ? "" : "s"
              }}
            </div>
            <div class="dry-diff-row" v-for="ch in c.changes" :key="ch.key">
              <span class="dry-diff-key">{{ ch.key }}</span>
              <span class="dry-diff-from" v-if="ch.from">{{ ch.from }}</span>
              <span class="dry-diff-from is-empty" v-else>(empty)</span>
              <span class="dry-diff-arrow">→</span>
              <span class="dry-diff-to">{{ ch.to || "(empty)" }}</span>
            </div>
          </div>
        </div>
        <!-- How the last bulk run really ended. The category percentage counts
             filled values, not successful requests, so it cannot say this. -->
        <div
          class="ai-summary"
          :class="{ bad: aiSummary.failures.length > 0 }"
          v-if="aiSummary"
        >
          <span>
            {{ aiSummary.done - aiSummary.failures.length }}/{{
              aiSummary.total
            }}
            succeeded<span v-if="aiSummary.stopped"> — stopped early</span
            ><span v-if="aiSummary.failures.length">
              , {{ aiSummary.failures.length }} failed</span
            >
          </span>
          <span class="ai-summary-keys" v-if="aiSummary.failures.length">
            {{ aiSummary.failures.map((f) => f.key).join(", ") }}
          </span>
          <span class="ai-summary-slow" v-if="aiSummary.slow?.length">
            {{ aiSummary.slow.length }} call{{
              aiSummary.slow.length === 1 ? "" : "s"
            }}
            over {{ Math.round(aiSummary.slow[0].ms / 1000) }}s — the service
            may be degrading
          </span>
          <button class="panel-close" v-on:click="aiSummary = null">
            Dismiss
          </button>
        </div>
        <!-- Only while there is something to decide on. It says plainly that
             accepting is not saving, because the two are easy to conflate when
             a whole category lights up at once. -->
        <div class="ai-review-bar" v-if="reviewCount > 0">
          <span class="ai-review-count">
            {{ reviewCount }} AI suggestion{{ reviewCount === 1 ? "" : "s" }} to
            review
          </span>
          <span class="ai-review-hint">
            Accepting only fills the field — press Save to write them.
          </span>
          <span class="ai-review-actions">
            <button class="ai-accept-all" v-on:click="acceptAllSuggestions">
              Accept all
            </button>
            <button class="ai-reject-all" v-on:click="rejectAllSuggestions">
              Reject all
            </button>
          </span>
        </div>
        <div class="tr-row tr-head" :class="{ 'no-key': !showKeys }">
          <span v-if="showKeys">Key</span>
          <span class="tr-en-head">
            <button
              class="key-toggle"
              :class="{ open: showKeys }"
              v-on:click="showKeys = !showKeys"
              :title="showKeys ? 'Hide keys' : 'Show keys'"
            >
              <svg
                viewBox="0 0 24 24"
                width="13"
                height="13"
                fill="currentColor"
              >
                <path d="M10 6l6 6-6 6z" />
              </svg>
            </button>
            English
          </span>
          <span>{{ activeLanguage?.name }}</span>
        </div>
        <div class="tr-rows">
          <div
            class="tr-row"
            :class="{ 'no-key': !showKeys }"
            v-for="word in filteredWords"
            :key="word.key"
          >
            <span class="tr-key" :title="word.key" v-if="showKeys">
              {{ word.key }}
            </span>
            <span class="tr-en">
              <span
                class="tr-issue-dot"
                v-if="rowIssue(word)"
                :title="rowIssue(word)"
              >
                <svg
                  viewBox="0 0 24 24"
                  width="13"
                  height="13"
                  fill="currentColor"
                >
                  <path
                    d="M12 2l10 18H2L12 2zm0 5a1 1 0 0 0-1 1v5a1 1 0 0 0 2 0V8a1 1 0 0 0-1-1zm0 9.5a1.25 1.25 0 1 0 0 2.5 1.25 1.25 0 0 0 0-2.5z"
                  />
                </svg>
              </span>
              {{ word.text_en ?? word.key }}
              <button
                v-if="codeContext"
                class="where-btn"
                :class="{ active: whereOpen[word.key] }"
                title="Where does this appear on the site?"
                v-on:click="showWhere(word)"
              >
                <svg
                  viewBox="0 0 24 24"
                  width="13"
                  height="13"
                  fill="currentColor"
                >
                  <path
                    d="M12 2a7 7 0 0 0-7 7c0 5.25 7 13 7 13s7-7.75 7-13a7 7 0 0 0-7-7zm0 9.5A2.5 2.5 0 1 1 12 6.5a2.5 2.5 0 0 1 0 5z"
                  />
                </svg>
              </button>
            </span>
            <div class="tr-target">
              <!-- A textarea, not an input: German runs half again as long as
                   English, and a one-line box cut the sentence off at the
                   bezel with nothing to say it went on. It grows to the text
                   and never scrolls, so the whole translation is on screen.
                   Enter is swallowed — these are single-line strings. -->
              <textarea
                rows="1"
                v-autogrow
                :class="{
                  missing: !word.text,
                  'ai-filled': aiFilled[word.key],
                  'ai-warn': aiWarn[word.key],
                  dirty: isDirty(word),
                }"
                :placeholder="word.text ? '' : $t('editor.missing')"
                v-model="word.text"
                v-on:input="clearAiFlags(word.key)"
                v-on:keydown.enter.prevent
              ></textarea>
              <button
                class="ai-btn"
                :class="{ busy: aiBusy[word.key] }"
                :disabled="aiBusy[word.key]"
                title="Translate with AI"
                v-on:click="aiTranslateRow(word)"
              >
                <span
                  class="app-loader small on-btn"
                  v-if="aiBusy[word.key]"
                ></span>
                <svg
                  v-else
                  viewBox="0 0 24 24"
                  width="15"
                  height="15"
                  fill="currentColor"
                >
                  <path
                    d="M12 2c.4 4.9 4.7 9.2 9.6 9.6v.8C16.7 12.8 12.4 17.1 12 22h-.8c-.4-4.9-4.7-9.2-9.6-9.6v-.8C6.5 11.2 10.8 6.9 11.2 2h.8z"
                  />
                </svg>
              </button>
              <span class="ai-warn-text" v-if="aiWarn[word.key]">
                {{ aiWarn[word.key] }}
              </span>
            </div>
            <!-- The AI's proposal, parked beside the row rather than in it.
                 When the row already had text, both are shown so the change is
                 the thing you judge, not the sentence on its own. -->
            <div
              class="tr-suggest"
              v-if="aiSuggestions[word.key] !== undefined"
            >
              <span class="tr-suggest-tag">AI</span>
              <span class="tr-suggest-body">
                <span class="tr-suggest-old" v-if="word.text">
                  {{ word.text }}
                </span>
                <span class="tr-suggest-new">{{
                  aiSuggestions[word.key]
                }}</span>
              </span>
              <span class="tr-suggest-actions">
                <button
                  class="tr-suggest-yes"
                  title="Use this translation"
                  v-on:click="acceptSuggestion(word)"
                >
                  <svg
                    viewBox="0 0 24 24"
                    width="14"
                    height="14"
                    fill="currentColor"
                  >
                    <path d="M9 16.2L4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4z" />
                  </svg>
                </button>
                <button
                  class="tr-suggest-no"
                  title="Discard this suggestion"
                  v-on:click="rejectSuggestion(word)"
                >
                  <svg
                    viewBox="0 0 24 24"
                    width="14"
                    height="14"
                    fill="currentColor"
                  >
                    <path
                      d="M19 6.4L17.6 5 12 10.6 6.4 5 5 6.4 10.6 12 5 17.6 6.4 19 12 13.4 17.6 19 19 17.6 13.4 12z"
                    />
                  </svg>
                </button>
              </span>
            </div>
            <div class="tr-where" v-if="whereOpen[word.key]">
              <span v-if="whereBusy[word.key]" class="tr-where-loading">
                Looking for it in the theme…
              </span>
              <template v-else-if="whereData[word.key] === 'none'">
                <span class="tr-where-none">
                  Not in the storefront theme — this is an app message (login,
                  checkout, validation), shown by the framework rather than a
                  template. Storefront categories like Products, Cart or Header
                  will point to the exact spot.
                </span>
              </template>
              <template v-else-if="whereData[word.key]">
                <div class="tr-where-head">
                  <span class="tr-where-loc">
                    {{ whereData[word.key].file }}:{{
                      whereData[word.key].line
                    }}
                  </span>
                  <span
                    class="tr-where-role"
                    v-if="whereData[word.key].element"
                  >
                    &lt;{{ whereData[word.key].element }}&gt;
                  </span>
                </div>
                <code class="tr-where-code">{{
                  whereData[word.key].block || whereData[word.key].snippet
                }}</code>
                <a
                  class="tr-where-live"
                  :class="{ 'not-visible': !bestPreview(word)?.visible }"
                  v-if="bestPreview(word)"
                  :href="liveUrl(bestPreview(word))"
                  target="_blank"
                  rel="noopener"
                >
                  <template v-if="bestPreview(word).visible">
                    See it on the storefront in {{ activeLanguage?.name }} —
                    {{ bestPreview(word).where }}
                  </template>
                  <template v-else>
                    Open the page ({{ activeLanguage?.name }}) —
                    {{ bestPreview(word).where }}. The text itself is not on
                    screen: it is a screen-reader label.
                  </template>
                </a>
                <div
                  class="tr-where-sibs"
                  v-if="
                    whereData[word.key].siblings &&
                    whereData[word.key].siblings.length
                  "
                >
                  Alongside:
                  <span
                    class="tr-where-sib"
                    v-for="s in whereData[word.key].siblings"
                    :key="s"
                    >{{ s }}</span
                  >
                </div>
              </template>
            </div>
          </div>
          <div class="empty-state" v-if="loadError">
            <div class="empty-title">Could not load this category</div>
            <div class="empty-text">
              The request failed — your session may have expired. Reload the
              page, or pick the category again.
            </div>
          </div>
          <div class="empty-state" v-else-if="userLoading">
            <div class="empty-text">Loading…</div>
          </div>
          <div class="empty-state" v-else-if="filteredWords.length === 0">
            <div class="empty-title">No matching keys</div>
            <div class="empty-text">
              Adjust the search or the “Only missing” filter.
            </div>
          </div>
        </div>
      </div>
      <!-- Languages mirror the Categories column: a vertical list reads more
           easily than a row once every entry carries a figure, and it has room
           for the full language name. -->
      <div class="menu-side lang-side">
        <div class="white-box">
          <h2 class="section-label side-head">Languages</h2>
          <div class="lang-scope" v-if="activePage?.name">
            in {{ activePage.name }}
          </div>
          <div
            class="lang-progress"
            v-if="measuredLanguages < languages.length"
          >
            <span v-if="measureStalled">
              measuring stalled — {{ measuredLanguages }}/{{ languages.length }}
            </span>
            <span v-else>
              measuring… {{ measuredLanguages }}/{{ languages.length }}
            </span>
          </div>
          <div class="side-list">
            <button
              v-for="language in languages"
              :key="language.code"
              v-on:click="activeLanguage = language"
              :class="{ active: activeLanguage?.code === language.code }"
              :title="languageTitle(language)"
            >
              <span class="lang-code">{{ language.code?.toUpperCase() }}</span>
              <span class="side-list-label">{{ language.name }}</span>
              <span
                class="cat-pct"
                :class="langPctClass(language)"
                v-if="languagePercent(language) !== null"
                >{{ languagePercent(language) }}%</span
              >
              <span class="cat-pct is-pending" v-else>–</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import HeaderBlock from "../components/header";
import http from "../modules/http";
import store from "../store/index";
import tenant from "../modules/tenant";

// How many AI requests a bulk run keeps in flight. One at a time made a
// two-hundred-key category take minutes; opening it wide trips the model's
// per-minute limit and turns half the run into errors.
const AI_CONCURRENCY = 3;

// Measured round trip is 4.4–7.6s per key. Anything past this is not yet a
// failure — the 45s timeout handles those — but it is the early shape of the
// service degrading, and a run that ends "all fine" while half its calls took
// half a minute has told you nothing useful.
const SLOW_CALL_MS = 20000;

// Sizes a translation box to its content. The height has to be cleared first,
// or the box can only ever grow: shortening the text would leave it standing at
// whatever the longest value needed.
function fitToContent(el) {
  el.style.height = "auto";
  el.style.height = el.scrollHeight + "px";
}

const AI_TIMEOUT_MS = 45000;
const HTML_ENTITY = /&(?:[a-zA-Z]{2,};?|#\d+;?)/;
const authHeaders = (json = false) => ({
  Accept: "application/json",
  Authorization: "Bearer " + store.state.token,
  ...(json ? { "Content-Type": "application/json" } : {}),
});
const extractPlaceholders = (text) => [
  ...new Set(String(text || "").match(/:[a-zA-Z_]\w*|\{[^}]+\}|%[sd]/g) || []),
];
const missingPlaceholders = (source, candidate) => {
  const have = new Set(extractPlaceholders(candidate));
  return extractPlaceholders(source).filter(
    (placeholder) => !have.has(placeholder)
  );
};
const translationIssue = (source, translation, usage) => {
  if (!translation || !translation.trim()) return "";
  const sourceSet = new Set(extractPlaceholders(source));
  const translatedSet = new Set(extractPlaceholders(translation));
  const missing = [...sourceSet].filter(
    (placeholder) => !translatedSet.has(placeholder)
  );
  const extra = [...translatedSet].filter(
    (placeholder) => !sourceSet.has(placeholder)
  );
  if (missing.length)
    return "Missing " + missing.join(", ") + " — will break at runtime";
  if (extra.length)
    return "Has " + extra.join(", ") + ", which the English does not";
  if (usage?.attributes?.length && HTML_ENTITY.test(translation)) {
    return (
      "HTML entity in a value the theme puts in " +
      usage.attributes.map((a) => a + "=").join(", ")
    );
  }
  return "";
};
const buildContext = ({
  word,
  category,
  file,
  targetCode,
  targetName,
  siblings,
}) => {
  const source = word.text_en ?? word.key;
  return {
    key: word.key,
    category,
    file,
    source,
    target_lang: targetCode,
    target_lang_name: targetName,
    placeholders: extractPlaceholders(source),
    siblings: siblings || {},
  };
};
async function aiAvailable() {
  try {
    const response = await fetch(tenant.getApiBaseUrl() + "/ai/status", {
      headers: authHeaders(),
    });
    if (!response.ok) return { available: false, codeContext: false };
    const data = await response.json();
    return {
      available: Boolean(data.available),
      codeContext: Boolean(data.codeContext),
    };
  } catch (e) {
    return { available: false, codeContext: false };
  }
}
async function whereUsed(file, key) {
  try {
    const response = await fetch(
      tenant.getApiBaseUrl() +
        "/ai/where?file=" +
        encodeURIComponent(file || "") +
        "&key=" +
        encodeURIComponent(key || ""),
      { headers: authHeaders() }
    );
    if (!response.ok) return null;
    return (await response.json()).usage || null;
  } catch (e) {
    return null;
  }
}
async function aiSuggest(payload) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), AI_TIMEOUT_MS);
  let response;
  try {
    response = await fetch(tenant.getApiBaseUrl() + "/ai/translate", {
      method: "POST",
      headers: authHeaders(true),
      body: JSON.stringify(payload),
      signal: controller.signal,
    });
  } catch (e) {
    throw new Error(
      e?.name === "AbortError"
        ? "AI request timed out after 45s"
        : "Could not complete the translation"
    );
  } finally {
    clearTimeout(timer);
  }
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(
      data.error || "AI request failed (" + response.status + ")"
    );
  }
  const data = await response.json();
  if (!data.suggestion) throw new Error("AI returned nothing");
  return data.suggestion;
}

export default {
  name: "TranslatorPage",
  components: { HeaderBlock },
  directives: {
    // On mount and on every re-render of the row, so a value arriving from the
    // server or from an accepted AI suggestion sizes the box too — not only
    // what you type into it.
    autogrow: {
      mounted: (el) => fitToContent(el),
      updated: (el) => fitToContent(el),
    },
  },
  data() {
    return {
      pages: [],
      languages: [],
      activeLanguage: {},
      activePage: {},
      words: [],
      filter: "",
      onlyMissing: false,
      onlyIssues: false,
      // The key is a technical id most editors do not need to see, so it is
      // hidden until the arrow on the English column reveals it.
      showKeys: false,
      // completion per category, keyed by language then category id
      coverage: {},
      totals: {},
      wordCache: {},
      // AI translation state, keyed by translation key
      aiBusy: {},
      aiWarn: {},
      aiFilled: {},
      // Bulk AI: proposals waiting for a verdict, keyed by translation key. A
      // bulk run fills this instead of the inputs, so nothing on screen changes
      // until a row is accepted.
      aiSuggestions: {},
      // 'missing' | 'all' while a bulk run is going, null otherwise.
      aiBulkMode: null,
      aiProgress: { done: 0, total: 0 },
      aiStop: false,
      // What each row looked like when it arrived from the server, per language
      // and category. "Changed but not saved" is then derived from the row
      // rather than a flag we have to remember to set and to clear.
      baseline: {},
      translationVersions: {},
      // Whether the theme is checked out locally so "where is this used"
      // lookups work. Independent of AI availability.
      codeContext: false,
      // Cached code locations per key: { file, line, snippet } once looked up,
      // "none" if not found, undefined if never asked.
      whereData: {},
      whereBusy: {},
      whereOpen: {},
      // Which keys in this category the theme uses inside an attribute.
      usageMap: {},
      wordsToken: 0,
      userLoading: false,
      loadError: false,
      saving: false,
      saveError: "",
      // Dry run: build the payload, show the diff, send nothing. The editor
      // writes to the live storefront and the endpoint replaces the whole set
      // for a file and language, so being able to look before writing matters
      // more here than it would against a staging copy.
      // On by default, deliberately. Pressing Save then shows what would be
      // written and sends nothing; going live is a second, conscious act. The
      // cost of the extra click is one click — the cost of the other default is
      // an overwritten category on the revenue channel.
      dryRun: true,
      dryRunDiff: null,
      // Categories whose server-side values moved since this browser loaded
      // them — someone else edited the same category. Saving would replace
      // their work with a set that never contained it.
      saveConflicts: null,
      // How a bulk run actually ended. Per-row warnings are easy to scroll
      // past; a run that quietly failed on six of two hundred keys must say so
      // in one place, because the category percentage will not — it counts
      // filled values, not successful requests.
      aiSummary: null,
      measureStalled: false,
      coverageLoading: false,
      sweeping: false,
      importing: false,
      importSummary: null,
    };
  },
  computed: {
    // Proposals still awaiting a verdict. Counted over the whole category, not
    // the filtered view, so narrowing the filter cannot hide outstanding work.
    reviewCount() {
      return this.words.filter((w) => this.aiSuggestions[w.key] !== undefined)
        .length;
    },
    // Every unsaved row in the session, not only the open category — Save now
    // writes all of them, so the number has to promise exactly that.
    dirtyCount() {
      return this.pendingSaves().reduce((n, s) => n + s.rows.length, 0);
    },
    // Which categories those rows are in, for the Save button's tooltip: with
    // edits spread over several tabs the count alone does not say where.
    pendingSummary() {
      return this.pendingSaves()
        .map((s) => {
          const page = this.pages.find(
            (p) => String(p.id) === String(s.pageId)
          );
          return (
            (page?.name ?? s.pageId) +
            " · " +
            s.code +
            " (" +
            s.rows.length +
            ")"
          );
        })
        .join(", ");
    },
    filteredWords() {
      return this.words
        .filter((w) => !this.onlyMissing || !w.text)
        .filter((w) => !this.onlyIssues || this.rowIssue(w))
        .filter((w) => {
          if (this.filter.length === 0) {
            return true;
          }
          const f = this.filter.toLowerCase();
          return (
            w.key?.toLowerCase().includes(f) ||
            w.text_en?.toLowerCase().includes(f) ||
            w.text?.toLowerCase().includes(f)
          );
        });
    },
    // How many saved translations in this category look wrong — surfaced in the
    // header so a problem is visible on arrival, not only when scrolled past.
    issueCount() {
      return this.words.filter((w) => this.rowIssue(w)).length;
    },
    // The active category is recomputed from the rows on screen so the number
    // moves while you type, instead of showing what the server last sent.
    activeCoverage() {
      if (this.words.length === 0) {
        return null;
      }
      const done = this.words.filter((w) => w.text).length;
      return Math.round((done / this.words.length) * 100);
    },
    measuredLanguages() {
      return this.languages.filter((l) => this.languagePercent(l) !== null)
        .length;
    },
    liveTotals() {
      const stored = this.coverage[this.activeLanguage?.code];
      if (!stored || this.activeCoverage === null) {
        return null;
      }
      // the open category's figure moves as you type, so fold the live one in
      const merged = {
        ...stored,
        [this.activePage.id]: {
          done: this.words.filter((w) => w.text).length,
          total: this.words.length,
        },
      };
      return this.totalFrom(merged);
    },
    donePercent() {
      if (this.words.length === 0) {
        return 0;
      }
      const done = this.words.filter(
        (w) => w.text !== undefined && w.text !== null && w.text !== ""
      ).length;
      return Math.round((done / this.words.length) * 100);
    },
  },
  mounted() {
    this.load();
    // AI is always available; the status call only tells whether the theme
    // code is checked out for the "where is this used" lookups.
    aiAvailable().then((status) => {
      this.codeContext = status.codeContext;
    });
  },
  watch: {
    activePage() {
      if (this.activeLanguage?.code !== undefined) {
        this.loadWords();
      }
    },
    activeLanguage() {
      if (this.activePage?.id !== undefined) {
        this.loadWords();
      }
      this.loadCoverage();
    },
    pages() {
      this.loadCoverage();
    },
  },
  methods: {
    // A saved translation that looks wrong (placeholder mismatch), or "".
    rowIssue(word) {
      return translationIssue(
        word.text_en ?? word.key,
        word.text,
        this.usageMap[word.key]
      );
    },
    // The live storefront page where a key appears, in the language currently
    // being edited — so a native reviewer sees the string in place rather than
    // judging it out of context. Currency is part of the path on this shop.
    // The usage index sees every occurrence of a key; the code lookup sees only
    // the first. For the link, the index wins — it is the one that knows which
    // occurrence a human can actually read.
    bestPreview(word) {
      return (
        this.usageMap[word.key]?.preview ||
        this.whereData[word.key]?.preview ||
        null
      );
    },
    liveUrl(preview) {
      if (!preview) {
        return "";
      }
      const lang = this.activeLanguage?.code || "en";
      const currency = { hu: "huf", ro: "ron" }[lang] || "eur";
      return "https://vivienvance.com/" + lang + "/" + currency + preview.path;
    },
    // Changed since it arrived from the server. Derived, so typing, pasting and
    // accepting an AI proposal all mark the row the same way.
    isDirty(word) {
      const snap =
        this.baseline[this.activeLanguage?.code]?.[this.activePage?.id];
      if (!snap) {
        return false;
      }
      return (word.text ?? "") !== (snap[word.key] ?? "");
    },
    // The same comparison for any cached category, not just the open one.
    dirtyRowsIn(code, pageId) {
      const rows = this.wordCache[code]?.[pageId];
      const snap = this.baseline[code]?.[pageId];
      if (!rows || !snap) {
        return [];
      }
      return rows.filter((r) => (r.text ?? "") !== (snap[r.key] ?? ""));
    },
    // Every language+category still holding unsaved rows. The row cache keeps
    // edits alive across switches on purpose, so this is the real extent of
    // what is only in the browser — and what Save has to write.
    pendingSaves() {
      const out = [];
      for (const code of Object.keys(this.wordCache)) {
        for (const pageId of Object.keys(this.wordCache[code])) {
          const rows = this.dirtyRowsIn(code, pageId);
          if (rows.length > 0) {
            out.push({ code, pageId, rows });
          }
        }
      }
      return out;
    },
    // ---- AI translation ----
    // Same key in the other languages that already have it — sent as context
    // so the model stays consistent across languages.
    aiSiblings(word) {
      const out = {};
      for (const lang of this.languages) {
        const code = lang.code;
        if (code === this.activeLanguage?.code) {
          continue;
        }
        const rows = this.wordCache[code]?.[this.activePage?.id];
        const match = rows?.find((r) => r.key === word.key);
        if (match?.text) {
          out[code] = match.text;
        }
      }
      return out;
    },
    clearAiFlags(key) {
      if (this.aiFilled[key]) {
        delete this.aiFilled[key];
      }
      if (this.aiWarn[key]) {
        delete this.aiWarn[key];
      }
    },
    // Toggle the "where on the site does this appear" panel for a row. The
    // lookup is cached, so reopening is instant and never re-hits the server.
    async showWhere(word) {
      if (this.whereOpen[word.key]) {
        delete this.whereOpen[word.key];
        return;
      }
      this.whereOpen[word.key] = true;
      if (this.whereData[word.key] !== undefined || this.whereBusy[word.key]) {
        return;
      }
      this.whereBusy[word.key] = true;
      try {
        const usage = await whereUsed(this.activePage?.file, word.key);
        this.whereData[word.key] = usage || "none";
      } catch (e) {
        this.whereData[word.key] = "none";
      } finally {
        delete this.whereBusy[word.key];
      }
    },
    // One proposal for one row: { suggestion } or { warn }. Shared by the row
    // button and the bulk runs, so the placeholder guard cannot drift apart
    // between the two paths.
    async fetchSuggestion(word) {
      const context = buildContext({
        word,
        category: this.activePage?.name,
        file: this.activePage?.file,
        targetCode: this.activeLanguage?.code,
        targetName: this.activeLanguage?.name,
        siblings: this.aiSiblings(word),
      });
      const suggestion = await aiSuggest(context);
      // A dropped placeholder would break the string at runtime, so the
      // suggestion is refused rather than offered when one is missing.
      const missing = missingPlaceholders(context.source, suggestion);
      if (missing.length) {
        return {
          warn: "AI dropped " + missing.join(", ") + " — left unchanged",
        };
      }
      return { suggestion };
    },
    // The per-row button still fills the field directly: you asked for that one
    // string and are looking straight at it. Only bulk runs go through review.
    async aiTranslateRow(word) {
      if (this.aiBusy[word.key]) {
        return;
      }
      this.aiBusy[word.key] = true;
      delete this.aiWarn[word.key];
      try {
        const { suggestion, warn } = await this.fetchSuggestion(word);
        if (warn) {
          this.aiWarn[word.key] = warn;
          return;
        }
        word.text = suggestion;
        this.aiFilled[word.key] = true;
      } catch (e) {
        // The server's own words when it has any — "came back cut short" is
        // worth reading, and "failed, try again" hides it.
        this.aiWarn[word.key] =
          e?.message || "AI translation failed — try again";
      } finally {
        delete this.aiBusy[word.key];
      }
    },
    // 'missing' fills the blanks; 'all' asks for a second opinion on every row
    // on screen. Both land in aiSuggestions for review — neither writes into a
    // field, and neither saves.
    async aiBulkRun(mode) {
      if (this.aiBulkMode) {
        return;
      }
      const targets =
        mode === "missing"
          ? this.filteredWords.filter((w) => !w.text)
          : this.filteredWords.slice();
      if (targets.length === 0) {
        return;
      }
      this.aiBulkMode = mode;
      this.aiStop = false;
      this.aiSummary = null;
      this.aiProgress = { done: 0, total: targets.length };
      // A shared queue drained by a few workers, rather than fixed slices: one
      // slow row then holds up only its own worker, not a whole batch.
      const queue = targets.slice();
      const failures = [];
      const slow = [];
      const worker = async () => {
        while (queue.length > 0 && !this.aiStop) {
          const word = queue.shift();
          this.aiBusy[word.key] = true;
          delete this.aiWarn[word.key];
          try {
            const startedAt = Date.now();
            const { suggestion, warn } = await this.fetchSuggestion(word);
            const took = Date.now() - startedAt;
            if (took > SLOW_CALL_MS) {
              slow.push({ key: word.key, ms: took });
            }
            if (warn) {
              this.aiWarn[word.key] = warn;
              failures.push({ key: word.key, why: warn });
            } else if ((suggestion ?? "").trim() !== (word.text ?? "").trim()) {
              this.aiSuggestions[word.key] = suggestion;
            }
            // A re-translation identical to what is already there gets no
            // strip: it would be noise in a list you have to work through.
          } catch (e) {
            // The server's own words when it has any — "came back cut short" is
            // worth reading, and "failed, try again" hides it.
            const why = e?.message || "AI translation failed — try again";
            this.aiWarn[word.key] = why;
            failures.push({ key: word.key, why });
          } finally {
            delete this.aiBusy[word.key];
            this.aiProgress.done += 1;
          }
        }
      };
      try {
        await Promise.all(
          Array.from(
            { length: Math.min(AI_CONCURRENCY, targets.length) },
            worker
          )
        );
      } finally {
        // Read before the flag is cleared: a stopped run is not a finished one,
        // and the difference has to survive into the summary.
        const stopped = this.aiStop;
        this.aiSummary = {
          total: targets.length,
          done: this.aiProgress.done,
          failures,
          slow,
          stopped,
        };
        this.aiBulkMode = null;
        this.aiStop = false;
      }
    },
    acceptSuggestion(word) {
      const suggestion = this.aiSuggestions[word.key];
      if (suggestion === undefined) {
        return;
      }
      word.text = suggestion;
      this.aiFilled[word.key] = true;
      delete this.aiSuggestions[word.key];
    },
    rejectSuggestion(word) {
      delete this.aiSuggestions[word.key];
    },
    // Over every row of the category, not the filtered view: a proposal made
    // before the filter changed would otherwise be stranded out of reach of its
    // own buttons.
    acceptAllSuggestions() {
      for (const word of this.words) {
        this.acceptSuggestion(word);
      }
    },
    rejectAllSuggestions() {
      this.aiSuggestions = {};
    },
    // Scoped to the category you have open: picking a category and seeing
    // which languages are missing it is the actual job. The whole-language
    // figure is still there, in the tooltip.
    languagePercent(language) {
      const code = language?.code;
      if (!code || !this.activePage?.id) {
        return null;
      }
      if (code === this.activeLanguage?.code && this.words.length > 0) {
        return this.activeCoverage;
      }
      return this.pctOf(this.coverage[code]?.[this.activePage.id]);
    },
    languageOverall(language) {
      const code = language?.code;
      if (!code) {
        return null;
      }
      if (code === this.activeLanguage?.code && this.liveTotals !== null) {
        return this.liveTotals;
      }
      return this.totals[code] ?? null;
    },
    languageTitle(language) {
      const here = this.languagePercent(language);
      const all = this.languageOverall(language);
      if (here === null) {
        return "Not measured yet";
      }
      const cat = this.activePage?.name ?? "this category";
      const base =
        all === null
          ? `${here}% of ${cat}`
          : `${here}% of ${cat} · ${all}% overall`;
      const surplus = this.surplusOf(
        this.coverage[language.code]?.[this.activePage?.id]
      );
      return surplus > 0
        ? `${base} · ${surplus} translated key(s) the source no longer has`
        : base;
    },
    langPctClass(language) {
      const pct = this.languagePercent(language);
      if (pct === null) {
        return "";
      }
      if (pct >= 100) {
        return "is-done";
      }
      return pct === 0 ? "is-empty" : "is-partial";
    },
    // The weakest language, not an average across them. This column exists to
    // answer "where is work left" the moment you arrive, and an average hides
    // exactly what you are looking for: a category can read 94% while one
    // language is missing half of it, because the others carry the mean up.
    // Only 100% here means every language is done.
    categoryWorst(page) {
      if (!page?.id) {
        return null;
      }
      let worst = null;
      for (const [code, byCategory] of Object.entries(this.coverage)) {
        const entry = byCategory?.[page.id];
        if (!entry || entry.total === undefined) {
          continue;
        }
        // the open category in the open language is being edited right now
        const pct =
          code === this.activeLanguage?.code && page.id === this.activePage?.id
            ? this.activeCoverage ?? this.pctOf(entry)
            : this.pctOf(entry);
        if (pct === null) {
          continue;
        }
        if (worst === null || pct < worst.pct) {
          worst = { pct, code };
        }
      }
      return worst;
    },
    categoryPercent(page) {
      return this.categoryWorst(page)?.pct ?? null;
    },
    categoryTitle(page) {
      const worst = this.categoryWorst(page);
      if (!worst) {
        return "Not measured yet";
      }
      const name =
        this.languages.find((l) => l.code === worst.code)?.name ?? worst.code;
      return worst.pct >= 100
        ? "Complete in every language"
        : `Lowest: ${name} at ${worst.pct}%`;
    },
    coverageClass(page) {
      const pct = this.categoryPercent(page);
      if (pct === null) {
        return "";
      }
      if (pct >= 100) {
        return "is-done";
      }
      return pct === 0 ? "is-empty" : "is-partial";
    },
    // One request per category, so this runs once per language and is cached.
    // Kept to three at a time: the editor API rate-limits hard and firing
    // twenty at once trips it.
    async loadCoverage() {
      this.coverageLoading = true;
      try {
        const response = await http.fetch("/translation/coverage");
        const data = response?.data ?? {};
        this.coverage = data.coverage ?? {};
        this.totals = data.totals ?? {};
      } finally {
        this.coverageLoading = false;
      }
    },
    // Weighted by keys, not by category. Averaging the per-category
    // percentages let a three-key category count as much as a two-hundred-key
    // one, which is not what "how translated is this language" means.
    totalFrom(perCategory) {
      const values = Object.values(perCategory);
      if (values.length === 0) {
        return null;
      }
      const done = values.reduce((a, v) => a + (v?.done ?? 0), 0);
      const total = values.reduce((a, v) => a + (v?.total ?? 0), 0);
      return total === 0
        ? 100
        : Math.min(100, Math.round((done / total) * 100));
    },
    pctOf(entry) {
      if (!entry || entry.total === undefined) {
        return null;
      }
      if (entry.total === 0) {
        return 100;
      }
      // Capped at 100: a translation file can hold keys the source language no
      // longer has — leftovers from features that were removed — and those
      // pushed the figure above 100% (German read 333%), which looks like a
      // broken counter rather than the surplus it is. The surplus itself is
      // reported separately, see surplusOf.
      return Math.min(100, Math.round((entry.done / entry.total) * 100));
    },
    // How many translated keys have no counterpart in the source language.
    // Worth saying out loud: they are dead weight in the file, and they are
    // why a language can look "more than finished".
    surplusOf(entry) {
      if (!entry || entry.total === undefined) {
        return 0;
      }
      return Math.max(0, (entry.done ?? 0) - entry.total);
    },
    // Measuring a language costs one request per category, so the others are
    // filled in afterwards, one language at a time, rather than firing a
    // hundred requests the moment the page opens. Any failure stops the sweep
    // — most likely the API's rate limit, and pushing on would only make it
    // worse.
    // The column you are looking at fills first: every language for the open
    // category is eight requests, against the hundred-odd a full sweep takes.
    // Without this the language figures trickle in over a minute and the empty
    // rows read as a fault rather than as work in progress.
    load() {
      http.fetch("/translation/categories").then((data) => {
        this.pages = data.data;
        this.activePage = this.pages[0];
      });
      http.fetch("/languages").then((data) => {
        this.languages = data;
        this.activeLanguage = this.languages[0];
      });
    },
    // The sweep downloads exactly these rows to count them, so they are kept
    // and reused: revisiting a language/category costs no request at all, and
    // unsaved edits survive switching away and back.
    cacheRows(code, pageId, rows) {
      if (!this.wordCache[code]) {
        this.wordCache[code] = {};
      }
      this.wordCache[code][pageId] = rows;
      // Snapshot the saved state the first time these rows are seen. The cache
      // deliberately keeps unsaved edits across switches, so re-snapshotting on
      // every visit would quietly clear the "changed" marks it exists to keep.
      if (!this.baseline[code]) {
        this.baseline[code] = {};
      }
      if (!this.baseline[code][pageId]) {
        const snap = {};
        for (const row of rows) {
          snap[row.key] = row.text ?? "";
        }
        this.baseline[code][pageId] = snap;
      }
    },
    loadWords() {
      const code = this.activeLanguage?.code;
      const page = this.activePage;
      if (!code || !page?.id) {
        return;
      }
      // Proposals belong to the view they were made in — carrying them over
      // would hang one category's suggestions on another category's rows.
      this.aiSuggestions = {};
      this.usageMap = {};
      const cached = this.wordCache[code]?.[page.id];
      if (cached) {
        this.words = cached;
        return;
      }
      // Only the newest request may paint. Without this a slower earlier
      // response could land last and leave one language's text under another
      // language's heading.
      const token = ++this.wordsToken;
      this.userLoading = true;
      // Empty it first: leaving the previous category's rows up while the new
      // ones load reads as "switching did nothing", and if the request then
      // fails they stay there under the new category's name.
      this.words = [];
      this.loadError = false;
      http
        .fetch(`/translation/${code}/${page.file}/en`)
        .then((data) => {
          const rows = data?.data?.translations ?? [];
          if (!this.translationVersions[code])
            this.translationVersions[code] = {};
          this.translationVersions[code][page.id] = data?.data?.version ?? 0;
          this.cacheRows(code, page.id, rows);
          if (token === this.wordsToken) {
            this.words = rows;
          }
        })
        .catch(() => {
          if (token === this.wordsToken) {
            this.loadError = true;
          }
        })
        .finally(() => {
          if (token === this.wordsToken) {
            this.userLoading = false;
          }
        });
    },
    // ---- JSON import ----
    // A reviewed translation set (docs/forditas/<lang>.json in the theme repo)
    // goes in the way a person would type it: rows change in the cache, the
    // Save button counts them, the dry run lists every from → to. Keys the
    // server does not have yet are added as new rows — the save endpoint
    // creates them. A category the server does not know cannot be created
    // from here (that is translation:find's job), so those keys are reported.
    async importJson(event) {
      const file = event.target.files?.[0];
      event.target.value = "";
      if (!file || this.importing) {
        return;
      }
      let data;
      try {
        data = JSON.parse(await file.text());
      } catch (e) {
        this.saveError = "Import: " + file.name + " is not valid JSON.";
        return;
      }
      const stem = file.name.replace(/\.json$/i, "").toLowerCase();
      const lang =
        this.languages.find((l) => l.code?.toLowerCase() === stem)?.code ||
        this.activeLanguage?.code;
      if (!lang || typeof data !== "object" || Array.isArray(data)) {
        return;
      }
      const byFile = {};
      for (const [full, text] of Object.entries(data)) {
        const dot = full.indexOf(".");
        if (dot < 1 || typeof text !== "string") {
          continue;
        }
        const group = full.slice(0, dot);
        (byFile[group] = byFile[group] || {})[full.slice(dot + 1)] = text;
      }
      const summary = {
        lang,
        changed: 0,
        added: 0,
        same: 0,
        noCategory: [],
        failed: [],
      };
      this.importing = true;
      this.importSummary = null;
      try {
        for (const [group, rows] of Object.entries(byFile)) {
          const page = this.pages.find(
            (p) => String(p.file || "").replace(/\.php$/, "") === group
          );
          if (!page) {
            summary.noCategory.push(group);
            continue;
          }
          let cached = this.wordCache[lang]?.[page.id];
          if (!cached) {
            try {
              const res = await http.fetch(
                `/translation/${lang}/${page.file}/en`
              );
              cached = res?.data?.translations ?? [];
              if (!this.translationVersions[lang])
                this.translationVersions[lang] = {};
              this.translationVersions[lang][page.id] = res?.data?.version ?? 0;
              this.cacheRows(lang, page.id, cached);
            } catch (e) {
              summary.failed.push(group);
              continue;
            }
          }
          for (const [key, text] of Object.entries(rows)) {
            const row = cached.find((r) => r.key === key);
            if (!row) {
              cached.push({ key, text });
              summary.added++;
            } else if ((row.text ?? "") !== text) {
              row.text = text;
              summary.changed++;
            } else {
              summary.same++;
            }
          }
        }
      } finally {
        this.importing = false;
      }
      this.importSummary = summary;
      if (lang === this.activeLanguage?.code) {
        this.words = this.wordCache[lang]?.[this.activePage?.id] ?? this.words;
      }
    },
    // Writes every category holding unsaved rows, not just the one on screen.
    // The row cache deliberately keeps edits alive when you switch away, so
    // saving only the open tab dropped the rest without a word. One request per
    // language+category, because that is the shape of the endpoint.
    async save() {
      if (this.saving) {
        return;
      }
      const pending = this.pendingSaves();
      if (pending.length === 0) {
        return;
      }
      this.saving = true;
      this.saveError = "";
      this.dryRunDiff = null;
      this.saveConflicts = null;
      const failed = [];
      const preview = [];
      const conflicts = [];
      for (const item of pending) {
        const page = this.pages.find(
          (p) => String(p.id) === String(item.pageId)
        );
        if (!page) {
          // Without the file name there is nothing sensible to post. Name it
          // rather than skipping it quietly — that was the original bug.
          failed.push("category " + item.pageId);
          continue;
        }
        const rows = this.wordCache[item.code]?.[item.pageId] ?? [];
        const baseline = this.baseline[item.code]?.[item.pageId] ?? {};
        // What the server holds right now, read at the last possible moment.
        // The client baseline is only what THIS browser was told on load; if a
        // colleague has edited the same category since, posting the whole set
        // would replace their work with a version that never contained it —
        // and a snapshot taken from the stale baseline could not restore it
        // either. So the truth comes from the server, not from memory.
        const live = await this.fetchServerRows(item.code, page.file);
        if (live === null) {
          failed.push(
            page.name + " · " + item.code + " (could not read current state)"
          );
          continue;
        }
        const before = Object.fromEntries(
          live.map((r) => [r.key, r.text ?? ""])
        );
        const drifted = [
          ...new Set([...Object.keys(before), ...Object.keys(baseline)]),
        ].filter((k) => (before[k] ?? "") !== (baseline[k] ?? ""));
        if (drifted.length > 0) {
          conflicts.push({
            category: page.name,
            lang: item.code,
            keys: drifted,
          });
          continue;
        }
        const changes = item.rows.map((r) => ({
          key: r.key,
          from: before[r.key] ?? "",
          to: r.text ?? "",
        }));
        if (this.dryRun) {
          preview.push({ category: page.name, lang: item.code, changes });
          continue;
        }
        try {
          await http.fetch(`/translation/save`, {
            file_name: page.file,
            lang: item.code,
            name: page.name,
            version: this.translationVersions[item.code]?.[item.pageId] ?? 0,
            // The endpoint takes the whole set for that file and language, not
            // only what changed — same payload as before, just no longer
            // limited to whichever category happens to be open.
            // Only the two fields the endpoint actually reads. The full row
            // objects (id, category, language code, English reference…) cost
            // 7 form variables per row, and PHP drops everything past
            // max_input_vars (1000) — a ~145-row category got truncated
            // mid-row and failed validation with "rows.142.key is required".
            rows: rows
              .filter((e) => e.text !== undefined)
              .map((e) => ({ key: e.key, text: e.text })),
          });
          if (!this.translationVersions[item.code])
            this.translationVersions[item.code] = {};
          this.translationVersions[item.code][item.pageId] =
            (this.translationVersions[item.code][item.pageId] ?? 0) + 1;
          this.afterSaved(item.code, item.pageId);
        } catch (e) {
          failed.push(page.name + " · " + item.code);
        }
      }
      this.saving = false;
      if (this.dryRun) {
        this.dryRunDiff = preview;
      }
      if (conflicts.length > 0) {
        this.saveConflicts = conflicts;
      }
      if (failed.length > 0) {
        // The successful ones already dropped out of the count, so what is left
        // on screen is exactly what still needs another go.
        this.saveError =
          "Could not save " + failed.join(", ") + " — still unsaved.";
      }
    },
    // The category's current server-side rows, or null if they could not be
    // read. Null is not "empty" — the caller must not treat a failed read as
    // "nothing there", which would make every key look newly added.
    async fetchServerRows(code, file) {
      try {
        const data = await http.fetch(`/translation/${code}/${file}/en`);
        return data?.data?.translations ?? [];
      } catch (e) {
        return null;
      }
    },
    // Recount the open category in memory so the sidebar responds immediately.
    afterSaved(code, pageId) {
      const rows = this.wordCache[code]?.[pageId] ?? [];
      const forLang = { ...(this.coverage[code] ?? {}) };
      forLang[pageId] = {
        done: rows.filter((w) => w.text).length,
        total: rows.length,
      };
      this.coverage = { ...this.coverage, [code]: forLang };
      this.totals = { ...this.totals, [code]: this.totalFrom(forLang) };
      const snap = {};
      for (const row of rows) {
        snap[row.key] = row.text ?? "";
      }
      if (!this.baseline[code]) {
        this.baseline[code] = {};
      }
      this.baseline[code][pageId] = snap;
    },
  },
};
</script>
<style scoped>
.translate-grid {
  display: grid;
  grid-template-columns: 210px 1fr 210px;
  gap: 14px;
  align-items: start;
}
@media (max-width: 1180px) {
  /* stacked layout — pinning a full-width card would cover the content */
  .translate-grid .menu-side,
  .translate-grid .menu-side .white-box {
    position: static;
    max-height: none;
  }
  .translate-grid {
    grid-template-columns: 1fr;
  }
}
.menu-side .white-box {
  margin-bottom: 0;
}
/* Both side columns stay put while the key list scrolls. The app header is
   sticky at 56px, so they start below it. The card itself does not scroll —
   its heading stays pinned and only the list inside moves, so you can always
   see which column you are looking at. */
/* top: 0 — the scrolling element here is .page-container, which already
   begins below the sticky app header. An offset for the header pushed both
   columns 68px below the list they sit beside. */
.translate-grid .menu-side {
  position: sticky;
  top: 0;
  max-height: calc(100vh - 84px);
}
.translate-grid .menu-side .white-box {
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - 84px);
  min-height: 0;
}
.translate-grid .menu-side .side-list {
  overflow-y: auto;
  min-height: 0;
}
.side-head {
  display: block;
  margin-bottom: 6px;
  padding: 0 6px;
}
.side-list {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.side-list button {
  border: none;
  cursor: pointer;
  padding: 8px 10px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 700;
  background: transparent;
  color: #505354;
  display: flex;
  align-items: center;
  gap: 8px;
  text-align: left;
  width: 100%;
  transition: background 0.15s;
}
.side-list button:hover {
  background: #f1f5f9;
}
.side-list button.active {
  background: rgba(124, 58, 237, 0.12);
  color: #5b21b6;
}
.side-list-label {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
/* Says which category the figures beside each language refer to. */
.lang-scope {
  font-size: 11.5px;
  font-weight: 600;
  color: #94a3b8;
  margin: -6px 0 8px;
}
.lang-progress {
  font-size: 11px;
  font-weight: 600;
  color: #cbd5e1;
  margin: -4px 0 8px;
}
/* "not measured yet" has to look different from "nothing translated" */
.cat-pct.is-pending {
  background: transparent;
  color: #cbd5e1;
}
/* Language rows: a fixed-width code keeps the names aligned in a column. */
.lang-code {
  flex: none;
  width: 26px;
  font-size: 10.5px;
  font-weight: 700;
  color: #94a3b8;
  letter-spacing: 0.4px;
}
.side-list button.active .lang-code {
  color: #7c3aed;
}
/* while a language is being measured the old figures are dimmed rather than
   blanked — a list that empties out reads as "nothing is translated" */
.side-list button.stale .cat-pct {
  opacity: 0.35;
}

/* Completion per category, so you can see where the work is without
   opening each one. Colour carries the same message as the number for
   anyone scanning rather than reading. */
.cat-pct {
  flex: none;
  font-size: 11px;
  font-weight: 700;
  padding: 2px 7px;
  border-radius: 6px;
  background: #f1f5f9;
  color: #94a3b8;
}
.cat-pct.is-done {
  background: rgba(22, 163, 74, 0.12);
  color: #15803d;
}
.cat-pct.is-partial {
  background: rgba(217, 119, 6, 0.14);
  color: #b45309;
}
.cat-pct.is-empty {
  background: rgba(220, 38, 38, 0.1);
  color: #b91c1c;
}
.translate-main {
  min-width: 0;
  margin-bottom: 0;
}
/* The header packs a lot in — title, badge, two toggles, search, the AI
   button. Keep the controls in their own group that pushes right and wraps
   onto a second line rather than overflowing the card. Scoped so the shared
   .card-head is untouched. */
.translate-main .card-head {
  flex-wrap: wrap;
  row-gap: 10px;
}
.head-controls {
  margin-left: auto;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 10px;
  min-width: 0;
}
.missing-toggle {
  font-size: 12px;
  color: #505354;
  white-space: nowrap;
  gap: 7px;
}
.search-input {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 13px;
  font-weight: 600;
  width: 180px;
  box-sizing: border-box;
  outline-color: #7c3aed;
}
/* ---- Existing-translation issues ---- */
.tr-issue-dot {
  display: inline-flex;
  vertical-align: middle;
  margin-right: 5px;
  color: #d97706;
  cursor: help;
}
.issue-count {
  display: inline-grid;
  place-items: center;
  min-width: 17px;
  height: 17px;
  padding: 0 4px;
  margin-left: 5px;
  border-radius: 9px;
  background: rgba(217, 119, 6, 0.15);
  color: #b45309;
  font-size: 10.5px;
  font-weight: 700;
}

/* ---- AI translation controls ---- */
.tr-target {
  position: relative;
  display: flex;
  /* top, not centre: once a long translation makes the box two or three lines
     tall, a vertically centred AI button floats in the middle of nowhere. */
  align-items: flex-start;
  gap: 6px;
}
.tr-target input,
.tr-target textarea {
  flex: 1;
  min-width: 0;
}
/* Grows with its content rather than scrolling it out of sight — the height is
   set by v-autogrow. resize off: the row grid owns the width. */
.tr-target textarea {
  display: block;
  resize: none;
  overflow: hidden;
  font-family: inherit;
  line-height: 1.35;
}
.ai-btn {
  flex: none;
  width: 30px;
  height: 30px;
  border: 1px solid #e2e8f0;
  background: #fff;
  border-radius: 8px;
  color: #7c3aed;
  cursor: pointer;
  display: grid;
  place-items: center;
  transition: background 0.12s, border-color 0.12s;
}
.ai-btn:hover:not(:disabled) {
  background: rgba(124, 58, 237, 0.1);
  border-color: #c4b5fd;
}
.ai-btn:disabled {
  cursor: progress;
}
.tr-target input.ai-filled,
.tr-target textarea.ai-filled {
  border-color: #c4b5fd;
  background: rgba(124, 58, 237, 0.05);
}
.tr-target input.ai-warn,
.tr-target textarea.ai-warn {
  border-color: #fca5a5;
}
/* Unsaved. An inset bar rather than a border colour, so it still reads on a
   row that is simultaneously flagged missing or AI-filled. */
.tr-target input.dirty,
.tr-target textarea.dirty {
  box-shadow: inset 3px 0 0 #f59e0b;
}
.ai-warn-text {
  position: absolute;
  left: 2px;
  bottom: -15px;
  font-size: 10.5px;
  font-weight: 600;
  color: #c53030;
  white-space: nowrap;
}
.ai-all-btn {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  border: 1px solid #e2e8f0;
  background: #fff;
  border-radius: 9px;
  padding: 7px 13px;
  font-size: 13px;
  font-weight: 600;
  color: #5b21b6;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.12s, border-color 0.12s;
}
.ai-all-btn:hover:not(:disabled) {
  background: rgba(124, 58, 237, 0.08);
  border-color: #c4b5fd;
}
.ai-all-btn:disabled {
  color: #94a3b8;
  cursor: not-allowed;
}
.ai-all-btn .on-btn,
.ai-btn .on-btn {
  width: 15px;
  background: radial-gradient(farthest-side, #7c3aed 94%, #0000) top/3px 3px
      no-repeat,
    conic-gradient(#0000 30%, #7c3aed);
  -webkit-mask: radial-gradient(farthest-side, #0000 calc(100% - 3px), #000 0);
  mask: radial-gradient(farthest-side, #0000 calc(100% - 3px), #000 0);
}

/* The key column collapses out, leaving English + target. */
.tr-row.no-key {
  grid-template-columns: 1fr 1fr;
}
.tr-en-head {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}
.key-toggle {
  border: 0;
  background: transparent;
  color: #94a3b8;
  cursor: pointer;
  padding: 2px;
  line-height: 0;
  border-radius: 4px;
  transition: transform 0.15s, color 0.12s;
}
.key-toggle:hover {
  color: #7c3aed;
}
.key-toggle.open {
  transform: rotate(90deg);
  color: #7c3aed;
}

.tr-row {
  display: grid;
  grid-template-columns: 180px 1fr 1fr;
  gap: 0 12px;
  align-items: center;
  padding: 8px 10px;
  border-top: 1px solid #f1f5f9;
}
.tr-row:hover {
  background: #f1f5f9;
  border-radius: 8px;
}
.tr-head {
  font-size: 11px;
  font-weight: 700;
  color: #768692;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-top: none;
  border-bottom: 1px solid #e2e8f0;
  border-radius: 0;
}
.tr-head:hover {
  background: transparent;
}
.tr-key {
  font-size: 12px;
  font-weight: 700;
  color: #505354;
  font-family: monospace;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.tr-en {
  font-size: 13px;
  font-weight: 600;
  color: #768692;
}
.tr-row input,
.tr-row textarea {
  border: 1px solid #e2e8f0;
  border-radius: 7px;
  padding: 7px 9px;
  font-size: 12px;
  font-weight: 600;
  box-sizing: border-box;
  min-width: 0;
  outline-color: #7c3aed;
}
.tr-row input.missing,
.tr-row textarea.missing {
  border-color: #facece;
}

/* ---- Bulk AI review ---- */
/* Blue throughout, deliberately not the purple the AI buttons use: purple is
   "the AI did something", blue is "you still have to decide about it". */
.ai-review-btn {
  color: #1d4ed8;
  border-color: #bfdbfe;
  background: rgba(37, 99, 235, 0.06);
}
.ai-review-btn:hover:not(:disabled) {
  background: rgba(37, 99, 235, 0.12);
}
.ai-running {
  display: inline-flex;
  align-items: center;
  gap: 9px;
  padding: 6px 11px;
  border: 1px solid #e2e8f0;
  border-radius: 9px;
  background: #f8fafc;
}
.ai-progress {
  font-size: 12px;
  font-weight: 700;
  color: #505354;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
.ai-stop-btn {
  border: 1px solid #e2e8f0;
  background: #fff;
  border-radius: 7px;
  padding: 3px 9px;
  font-size: 11.5px;
  font-weight: 700;
  color: #c53030;
  cursor: pointer;
}
.ai-stop-btn:hover:not(:disabled) {
  border-color: #fca5a5;
  background: #fff5f5;
}
.ai-stop-btn:disabled {
  color: #a0aec0;
  cursor: default;
}
.ai-review-bar {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  margin-top: 10px;
  padding: 8px 12px;
  border: 1px solid #bfdbfe;
  border-radius: 9px;
  background: rgba(37, 99, 235, 0.06);
}
.ai-review-count {
  font-size: 12.5px;
  font-weight: 700;
  color: #1e40af;
}
.ai-review-hint {
  font-size: 11.5px;
  font-weight: 600;
  color: #64748b;
}
.ai-review-actions {
  margin-left: auto;
  display: inline-flex;
  gap: 7px;
}
.ai-accept-all,
.ai-reject-all {
  border-radius: 7px;
  padding: 5px 11px;
  font-size: 11.5px;
  font-weight: 700;
  cursor: pointer;
}
.ai-accept-all {
  border: 1px solid #86efac;
  background: #f0fdf4;
  color: #15803d;
}
.ai-accept-all:hover {
  background: #dcfce7;
}
.ai-reject-all {
  border: 1px solid #e2e8f0;
  background: #fff;
  color: #64748b;
}
.ai-reject-all:hover {
  border-color: #cbd5e1;
  background: #f8fafc;
}
/* The proposal, parked under its row. Spans the full grid so a long string has
   room, on a tint that cannot be mistaken for an editable field. */
.tr-suggest {
  grid-column: 1 / -1;
  display: flex;
  align-items: flex-start;
  gap: 9px;
  margin-top: 7px;
  padding: 7px 10px;
  border: 1px solid #bfdbfe;
  border-left: 3px solid #3b82f6;
  border-radius: 8px;
  background: rgba(37, 99, 235, 0.05);
}
.tr-suggest-tag {
  flex: none;
  margin-top: 1px;
  padding: 1px 6px;
  border-radius: 5px;
  background: #3b82f6;
  color: #fff;
  font-size: 9.5px;
  font-weight: 800;
  letter-spacing: 0.5px;
}
.tr-suggest-body {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
/* The value being replaced, struck through: what you are judging is the
   change, not the new sentence on its own. */
.tr-suggest-old {
  font-size: 10.5px;
  font-weight: 500;
  color: #b0bcc9;
  text-decoration: line-through;
  word-break: break-word;
}
.tr-suggest-new {
  font-size: 13px;
  font-weight: 700;
  color: #1e3a8a;
  word-break: break-word;
}
.tr-suggest-actions {
  flex: none;
  display: inline-flex;
  gap: 5px;
}
.tr-suggest-yes,
.tr-suggest-no {
  width: 26px;
  height: 26px;
  border-radius: 7px;
  border: 1px solid #e2e8f0;
  background: #fff;
  cursor: pointer;
  display: grid;
  place-items: center;
  transition: background 0.12s, border-color 0.12s, color 0.12s;
}
/* Green at rest, matching "Accept all" above rather than waiting for a hover:
   the affirmative action should read as one before you reach for it. */
.tr-suggest-yes {
  border-color: #86efac;
  background: #f0fdf4;
  color: #15803d;
}
.tr-suggest-yes:hover {
  background: #dcfce7;
  border-color: #4ade80;
}
.tr-suggest-no {
  color: #94a3b8;
}
.tr-suggest-no:hover {
  background: #fff5f5;
  border-color: #fca5a5;
  color: #c53030;
}
/* Unsaved-count pill on the header Save button. */
.save-count {
  display: inline-grid;
  place-items: center;
  min-width: 18px;
  height: 18px;
  margin-left: 7px;
  padding: 0 5px;
  border-radius: 9px;
  background: rgba(255, 255, 255, 0.25);
  font-size: 11px;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
}
.btn-primary.has-unsaved {
  box-shadow: 0 0 0 3px rgba(124, 58, 237, 0.18);
}
/* ---- Dry run, snapshots and run summaries ---- */
.dry-toggle.on {
  border-radius: 7px;
  padding: 3px 8px;
  background: #fffbeb;
  box-shadow: inset 0 0 0 1px #fcd34d;
  color: #92400e;
  font-weight: 700;
}
.panel-close {
  margin-left: auto;
  border: 1px solid #e2e8f0;
  background: #fff;
  border-radius: 6px;
  padding: 2px 9px;
  font-size: 11px;
  font-weight: 700;
  color: #64748b;
  cursor: pointer;
}
.panel-close:hover {
  border-color: #cbd5e1;
  background: #f8fafc;
}
.save-conflict {
  margin-top: 10px;
  padding: 10px 12px;
  border: 1px solid #fca5a5;
  border-radius: 9px;
  background: #fff5f5;
  font-size: 12.5px;
  color: #c53030;
}
.save-conflict-cat {
  margin-top: 6px;
}
.save-conflict-keys {
  font-family: monospace;
  font-size: 11px;
  font-weight: 700;
  word-break: break-word;
}
.save-conflict-hint {
  margin-top: 7px;
  font-size: 11.5px;
  font-weight: 600;
  color: #7b341e;
}
.dry-diff {
  margin-top: 10px;
  padding: 10px 12px;
  border: 1px solid #fcd34d;
  border-radius: 9px;
  background: #fffbeb;
}
.dry-diff-head {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 12.5px;
  color: #92400e;
}
.dry-diff-empty {
  margin-top: 6px;
  font-size: 12px;
  font-weight: 600;
  color: #92400e;
}
.dry-diff-cat {
  margin-top: 9px;
}
.dry-diff-cat-head {
  font-size: 11px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #a16207;
}
.dry-diff-row {
  display: flex;
  align-items: baseline;
  gap: 7px;
  flex-wrap: wrap;
  padding: 3px 0;
  font-size: 12px;
  border-top: 1px solid rgba(161, 98, 7, 0.15);
}
.dry-diff-key {
  font-family: monospace;
  font-size: 11px;
  font-weight: 700;
  color: #78350f;
  min-width: 140px;
}
.dry-diff-from {
  color: #a8a29e;
  text-decoration: line-through;
}
.dry-diff-from.is-empty {
  text-decoration: none;
  font-style: italic;
}
.dry-diff-arrow {
  color: #a16207;
}
.dry-diff-to {
  font-weight: 700;
  color: #713f12;
}
.ai-summary {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  margin-top: 10px;
  padding: 8px 12px;
  border: 1px solid #86efac;
  border-radius: 9px;
  background: #f0fdf4;
  font-size: 12.5px;
  font-weight: 700;
  color: #15803d;
}
.ai-summary.bad {
  border-color: #fca5a5;
  background: #fff5f5;
  color: #c53030;
}
.ai-summary-slow {
  font-size: 11.5px;
  font-weight: 700;
  color: #92400e;
}
.ai-summary-keys {
  font-family: monospace;
  font-size: 11px;
  font-weight: 600;
  opacity: 0.85;
  word-break: break-word;
}
.import-btn {
  font: inherit;
  font-size: 13px;
  padding: 6px 12px;
  border-radius: 8px;
  border: 1px solid #d6d6de;
  background: #fff;
  cursor: pointer;
}
.import-btn:disabled {
  opacity: 0.5;
  cursor: default;
}
.import-summary {
  position: relative;
  margin: 8px 0;
  padding: 10px 36px 10px 14px;
  border-radius: 10px;
  background: #eef4ff;
  font-size: 13px;
  line-height: 1.5;
}
.import-skip {
  display: block;
  color: #8a5a00;
}
.save-error {
  margin-top: 10px;
  padding: 8px 12px;
  border: 1px solid #fca5a5;
  border-radius: 9px;
  background: #fff5f5;
  font-size: 12.5px;
  font-weight: 700;
  color: #c53030;
}

/* ---- "Where is this used" code-context lookup ---- */
.where-btn {
  display: inline-flex;
  vertical-align: middle;
  margin-left: 6px;
  padding: 2px;
  border: none;
  background: transparent;
  color: #b0b8bf;
  border-radius: 5px;
  cursor: pointer;
  transition: color 0.12s, background 0.12s;
}
.where-btn:hover,
.where-btn.active {
  color: #7c3aed;
  background: rgba(124, 58, 237, 0.1);
}
.tr-where {
  grid-column: 1 / -1;
  margin-top: 8px;
  padding: 8px 10px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  gap: 5px;
}
.tr-where-loading,
.tr-where-none {
  font-size: 12px;
  color: #768692;
}
.tr-where-head {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}
.tr-where-loc {
  font-size: 11px;
  font-weight: 700;
  color: #7c3aed;
  font-family: monospace;
}
.tr-where-role {
  font-size: 11px;
  font-weight: 700;
  font-family: monospace;
  color: #0f766e;
  background: rgba(13, 148, 136, 0.1);
  padding: 1px 7px;
  border-radius: 6px;
}
.tr-where-code {
  font-size: 11.5px;
  color: #334155;
  font-family: monospace;
  white-space: pre-wrap;
  word-break: break-word;
  line-height: 1.5;
  max-height: 220px;
  overflow: auto;
  background: #fff;
  border: 1px solid #eef2f7;
  border-radius: 6px;
  padding: 8px 10px;
}
.tr-where-live {
  display: inline-block;
  margin-top: 7px;
  padding: 4px 10px;
  border: 1px solid #c4b5fd;
  border-radius: 7px;
  background: rgba(124, 58, 237, 0.06);
  font-size: 11.5px;
  font-weight: 700;
  color: #5b21b6;
  text-decoration: none;
}
.tr-where-live.not-visible {
  border-color: #cbd5e1;
  background: #f8fafc;
  color: #64748b;
}
.tr-where-live:hover {
  background: rgba(124, 58, 237, 0.14);
}
.tr-where-sibs {
  font-size: 11px;
  color: #768692;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 5px;
}
.tr-where-sib {
  font-family: monospace;
  font-weight: 600;
  color: #475569;
  background: #eef2f7;
  padding: 1px 6px;
  border-radius: 5px;
}
</style>
